import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  DocumentTextIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { deleteMeetingMinutes, fetchMeetingMinutes } from "../../../api/meetingMinutes";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import {
  MeetingMinutesShell,
  mmCardClass,
  mmPrimaryButtonClass,
  mmSecondaryButtonClass,
} from "./MeetingMinutesShell";
import { MeetingMinutesTypePills, meetingTypeLabel } from "./MeetingMinutesTypePills";
import RoleOnly from "../../../routes/roleOnly";

export const MeetingMinutesList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [meetingType, setMeetingType] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      const response = await fetchMeetingMinutes({
        query: searchQuery,
        meeting_type: meetingType || undefined,
        per_page: 50,
        page: 1,
      });
      setRows(response.data || []);
      setError(false);
    } catch (err) {
      console.error("Error fetching meeting minutes:", err);
      setError(true);
      toast.error(t("meeting_minutes.load_error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [searchQuery, meetingType]);

  const handleDelete = async (id) => {
    if (!window.confirm(t("meeting_minutes.delete_confirm"))) return;
    try {
      await deleteMeetingMinutes(id);
      toast.success(t("meeting_minutes.deleted"));
      load();
    } catch (err) {
      toast.error(t("meeting_minutes.delete_error"));
    }
  };

  const filtered = useMemo(() => rows, [rows]);

  if (error) return <ResourceError />;

  return (
    <MeetingMinutesShell
      eyebrow={<><DocumentTextIcon className="h-4 w-4" />{t("meeting_minutes.list")}</>}
      title={t("meeting_minutes.list")}
      subtitle={t("meeting_minutes.hero_subtitle")}
      actions={
        <>
          <RoleOnly role="meeting_minutes_templates">
            <Link to="/dashboard/meeting-minutes/templates" className={mmSecondaryButtonClass()}>
              {t("meeting_minutes.templates.list")}
            </Link>
          </RoleOnly>
          <button
            type="button"
            onClick={() => navigate("/dashboard/meeting-minutes/new")}
            className={mmPrimaryButtonClass()}
          >
            <PlusIcon className="h-4 w-4" />
            {t("meeting_minutes.new")}
          </button>
        </>
      }
    >
      <div className={mmCardClass()}>
        <div className="space-y-4 border-b border-slate-100 px-6 py-5">
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("meeting_minutes.search")}
            className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:border-[#1463C7] focus:outline-none focus:ring-2 focus:ring-[#1463C7]/20"
          />
          <MeetingMinutesTypePills value={meetingType} onChange={setMeetingType} allowEmpty />
        </div>

        {loading ? (
          <div className="px-6 py-10">
            <ResourceLoading />
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-6 py-12 text-sm text-slate-500">{t("meeting_minutes.no_results")}</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map((mm) => (
              <div key={mm.id} className="flex items-center justify-between gap-4 px-6 py-4 hover:bg-slate-50">
                <Link to={`/dashboard/meeting-minutes/${mm.id}`} className="min-w-0 flex-1">
                  <h3 className="truncate text-sm font-semibold text-slate-950">{mm.title}</h3>
                  <p className="mt-1 text-xs text-slate-500">
                    {meetingTypeLabel(t, mm.meeting_type)}
                    {mm.project ? ` · ${mm.project.displayTitle || mm.project.name}` : ""}
                    {mm.employee ? ` · ${mm.employee.displayTitle}` : ""}
                  </p>
                </Link>
                <button
                  type="button"
                  onClick={() => handleDelete(mm.id)}
                  className="rounded-full p-2 text-slate-400 hover:bg-red-50 hover:text-red-600"
                  title={t("meeting_minutes.delete")}
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </MeetingMinutesShell>
  );
};
